import { useMemo, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  InputSize,
  Show
} from "shared/components";

interface FilterSectionOption {
  id?: string;
  name: string;
  count?: number;
}

const matchesSearch = (options: FilterSectionOption[], search: string) => {
  const needle = search.toLowerCase();

  return options.filter((option) => option.name.toLowerCase().includes(needle));
};

export const FilterSection = ({
  title,
  options,
  optionLimit,
  selectedOptions,
  onChange,
  showSearch = true,
  onSearchChange,
  isLoading = false,
  formatLabel = (name: string) => name
}: {
  title: string;
  options: FilterSectionOption[];
  optionLimit?: number;
  selectedOptions: string[];
  onChange: (value: string) => void;
  showSearch?: boolean;
  onSearchChange?: (search: string) => void;
  isLoading?: boolean;
  formatLabel?: (name: string) => string;
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");

  const isServerSearch = Boolean(onSearchChange);

  const filteredOptions = useMemo(
    () => (isServerSearch ? options : matchesSearch(options, search)),
    [isServerSearch, options, search]
  );

  const selected = useMemo(() => new Set(selectedOptions), [selectedOptions]);

  const displayedOptions = useMemo(
    () =>
      showAll || !optionLimit
        ? filteredOptions
        : filteredOptions.slice(0, optionLimit),
    [showAll, optionLimit, filteredOptions]
  );

  const hasMoreThanLimit = Boolean(
    optionLimit && filteredOptions.length > optionLimit
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    onSearchChange?.(value);
  };

  return (
    <div className="px-2 py-4">
      <button
        type="button"
        aria-expanded={expanded}
        className="flex w-full cursor-pointer items-center justify-between"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <p className="text-sm font-bold text-content-1000">{title}</p>
        <HugeiconsIcon icon={expanded ? ArrowUp01Icon : ArrowDown01Icon} />
      </button>

      <Show if={expanded}>
        <div className="mt-4">
          <Show if={showSearch}>
            <BaseInput
              id={`filter-search-${title}`}
              autoComplete="off"
              placeholder="Search..."
              value={search}
              onChange={handleSearch}
              size={InputSize.SM}
              className="mb-4 h-12"
            />
          </Show>

          <Show if={isLoading}>
            <p className="text-sm text-content-400">Loading…</p>
          </Show>

          <Show if={!isLoading && !displayedOptions.length}>
            <p className="text-sm text-content-400">No options available</p>
          </Show>

          <div
            className="space-y-2"
            style={{
              maskImage:
                hasMoreThanLimit && !showAll
                  ? "linear-gradient(to bottom, rgba(0, 0, 0, 1.0) 0%, transparent 100%)"
                  : "none"
            }}
          >
            {displayedOptions.map((option) => (
              <Checkbox
                key={option.id ?? option.name}
                id={option.id ?? option.name}
                name={option.name}
                size={CheckboxSize.SM}
                checked={selected.has(option.id ?? option.name)}
                label={
                  option.count === undefined
                    ? formatLabel(option.name)
                    : `${formatLabel(option.name)} (${option.count})`
                }
                labelClassName="whitespace-normal"
                onChange={() => onChange(option.id ?? option.name)}
              />
            ))}
          </div>

          <Show if={hasMoreThanLimit}>
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.XS}
              className="mt-2 !px-0"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? "Show less" : "Show more"}
            </Button>
          </Show>
        </div>
      </Show>
    </div>
  );
};
